/**
 * @fileoverview Gouvernance des rôles et permissions backoffice.
 *
 * Cette page sépare la politique d'accès de la gestion des comptes agents.
 * Elle présente les presets de rôles et la matrice globale. La configuration
 * détaillée vit dans `/roles-permissions/:role` pour éviter de concentrer les
 * checkboxes et les agents sur la même page d'aperçu.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataCard,
  DataTable,
  EmptyState,
  FormField,
  Input,
  MetricCard,
  PageHeader,
  Checkbox,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@gnpc/ui';
import { Plus, Save, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  ALL_CAPABILITIES,
  BACKOFFICE_ROLES,
  CAPABILITY_GROUPS,
  CAPABILITY_LABELS,
  ROLE_CAPABILITIES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  type BackofficeRole,
  type Capability,
} from '../data/backoffice.ts';
import {
  CAPABILITY_ROWS,
  PermissionMark,
  roleTone,
  type CapabilityRow,
} from '../features/roles/rolePermissions.tsx';

interface RoleSummary {
  id: string;
  label: string;
  description: string;
  capabilities: Capability[];
  presetRole?: BackofficeRole;
  custom?: boolean;
}

interface RoleDraft {
  id: string | null;
  label: string;
  description: string;
  baseRole: BackofficeRole;
  capabilities: Capability[];
}

const DEFAULT_ROLE_DRAFT: RoleDraft = {
  id: null,
  label: '',
  description: '',
  baseRole: 'AGENT',
  capabilities: ROLE_CAPABILITIES.AGENT,
};

function getPresetRoles(): RoleSummary[] {
  return BACKOFFICE_ROLES.map((role) => ({
    id: role.toLowerCase(),
    label: ROLE_LABELS[role],
    description: ROLE_DESCRIPTIONS[role],
    capabilities: ROLE_CAPABILITIES[role],
    presetRole: role,
  }));
}

function getCustomRoleId(label: string): string {
  const slug = label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `custom-${slug || 'role'}-${Date.now()}`;
}

export function RolesPermissionsPage(): JSX.Element {
  const [customRoles, setCustomRoles] = useState<RoleSummary[]>([]);
  const [roleSheetOpen, setRoleSheetOpen] = useState(false);
  const [roleDraft, setRoleDraft] = useState<RoleDraft>(DEFAULT_ROLE_DRAFT);
  /** Confirmation avant application : la sauvegarde d'un rôle est sensible. */
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);

  const roleSummaries = useMemo(
    () => [...getPresetRoles(), ...customRoles],
    [customRoles],
  );

  const columns = useMemo<ColumnDef<CapabilityRow>[]>(
    () => [
      {
        accessorKey: 'label',
        header: 'Permission',
        cell: ({ row }) => (
          <p className="text-quipux-navy-950 max-w-xs font-medium">{row.original.label}</p>
        ),
      },
      {
        accessorKey: 'group',
        header: 'Famille',
        cell: ({ row }) => <Badge variant="outline">{row.original.group}</Badge>,
      },
      ...roleSummaries.map<ColumnDef<CapabilityRow>>((role) => ({
        id: role.id,
        header: role.label,
        cell: ({ row }) => (
          <PermissionMark allowed={role.capabilities.includes(row.original.capability)} />
        ),
      })),
    ],
    [roleSummaries],
  );

  const openCreateRoleSheet = () => {
    setRoleDraft({
      ...DEFAULT_ROLE_DRAFT,
      capabilities: [...ROLE_CAPABILITIES.AGENT],
    });
    setRoleSheetOpen(true);
  };

  const openEditCustomRoleSheet = (role: RoleSummary) => {
    setRoleDraft({
      id: role.id,
      label: role.label,
      description: role.description,
      baseRole: role.presetRole ?? 'AGENT',
      capabilities: [...role.capabilities],
    });
    setRoleSheetOpen(true);
  };

  const updateRoleBase = (baseRole: BackofficeRole) => {
    setRoleDraft((current) => ({
      ...current,
      baseRole,
      capabilities: [...ROLE_CAPABILITIES[baseRole]],
    }));
  };

  const toggleDraftCapability = (capability: Capability, checked: boolean) => {
    setRoleDraft((current) => ({
      ...current,
      capabilities: checked
        ? Array.from(new Set([...current.capabilities, capability]))
        : current.capabilities.filter((item) => item !== capability),
    }));
  };

  /**
   * Interception de la soumission : la sauvegarde d'un rôle est une action
   * sensible (elle définit des droits), on demande donc une confirmation via
   * `AlertDialog` avant d'appliquer réellement le brouillon.
   */
  const saveCustomRole = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!roleDraft.label.trim()) return;
    setConfirmSaveOpen(true);
  };

  /** Application effective du brouillon, après confirmation explicite. */
  const applyCustomRole = () => {
    const label = roleDraft.label.trim();
    if (!label) return;

    const nextRole: RoleSummary = {
      id: roleDraft.id ?? getCustomRoleId(label),
      label,
      description:
        roleDraft.description.trim() || 'Rôle personnalisé à documenter avant activation.',
      capabilities: roleDraft.capabilities,
      custom: true,
    };

    setCustomRoles((current) =>
      roleDraft.id
        ? current.map((role) => (role.id === roleDraft.id ? nextRole : role))
        : [...current, nextRole],
    );
    setConfirmSaveOpen(false);
    setRoleSheetOpen(false);
    toast.success(roleDraft.id ? 'Rôle mis à jour' : 'Rôle créé', {
      description: `${label} · ${roleDraft.capabilities.length} permission(s) — action journalisée.`,
    });
  };

  return (
    <>
      <div className="grid gap-6">
        <PageHeader
          eyebrow="Administration"
          title="Rôles et permissions"
          description="Définir les rôles et contrôler les permissions. La validation financière (approbation des remboursements) est isolée dans le rôle « Validateur financier QFS », séparée de l'administration technique."
          actions={
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button asChild variant="outline">
                <Link to="/users">
                  <Users className="size-4" />
                  Voir les agents
                </Link>
              </Button>
              <Sheet open={roleSheetOpen} onOpenChange={setRoleSheetOpen}>
                <SheetTrigger asChild>
                  <Button onClick={openCreateRoleSheet}>
                    <Plus className="size-4" />
                    Nouveau rôle
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-xl">
                  <form onSubmit={saveCustomRole}>
                    <SheetHeader className="border-border border-b p-5 pr-12">
                      <SheetTitle>
                        {roleDraft.id ? 'Modifier le rôle' : 'Nouveau rôle'}
                      </SheetTitle>
                      <SheetDescription>
                        Définir le libellé, la description et les permissions du rôle.
                      </SheetDescription>
                    </SheetHeader>

                    <div className="grid gap-4 p-5">
                      <FormField label="Nom du rôle" required>
                        <Input
                          value={roleDraft.label}
                          onChange={(event) =>
                            setRoleDraft((current) => ({ ...current, label: event.target.value }))
                          }
                          placeholder="Ex. Support financier"
                        />
                      </FormField>
                      <FormField label="Description">
                        <textarea
                          value={roleDraft.description}
                          onChange={(event) =>
                            setRoleDraft((current) => ({
                              ...current,
                              description: event.target.value,
                            }))
                          }
                          className="border-input bg-background min-h-24 w-full rounded-md border px-3 py-2 text-sm"
                          placeholder="Résumer le périmètre opérationnel du rôle."
                        />
                      </FormField>
                      <FormField label="Modèle de départ" required>
                        <select
                          value={roleDraft.baseRole}
                          onChange={(event) => updateRoleBase(event.target.value as BackofficeRole)}
                          className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                        >
                          {BACKOFFICE_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {ROLE_LABELS[role]}
                            </option>
                          ))}
                        </select>
                      </FormField>

                      <div className="grid gap-3">
                        <div>
                          <p className="text-quipux-navy-950 text-sm font-semibold">
                            Permissions
                          </p>
                          <p className="text-muted-foreground text-sm">
                            {roleDraft.capabilities.length}/{ALL_CAPABILITIES.length} permission(s) sélectionnée(s).
                          </p>
                        </div>
                        <div className="grid gap-3">
                          {CAPABILITY_GROUPS.map((group) => (
                            <div key={group.id} className="rounded-lg border border-border p-3">
                              <p className="text-quipux-navy-950 font-medium">{group.label}</p>
                              <p className="text-muted-foreground mt-1 text-sm">{group.description}</p>
                              <div className="mt-3 grid gap-2">
                                {group.capabilities.map((capability) => {
                                  const id = `draft-${group.id}-${capability}`;
                                  return (
                                    <label
                                      key={capability}
                                      htmlFor={id}
                                      className="flex items-start gap-3 rounded-md border border-border px-3 py-2"
                                    >
                                      <Checkbox
                                        id={id}
                                        checked={roleDraft.capabilities.includes(capability)}
                                        onCheckedChange={(value) =>
                                          toggleDraftCapability(capability, value === true)
                                        }
                                      />
                                      <span className="text-quipux-navy-950 text-sm font-medium leading-5">
                                        {CAPABILITY_LABELS[capability]}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <SheetFooter className="border-border border-t p-5">
                      <Button type="submit" disabled={roleDraft.label.trim().length === 0}>
                        <Save className="size-4" />
                        {roleDraft.id ? 'Enregistrer' : 'Créer le rôle'}
                      </Button>
                      <SheetClose asChild>
                        <Button type="button" variant="outline">Annuler</Button>
                      </SheetClose>
                    </SheetFooter>
                  </form>
                </SheetContent>
              </Sheet>
            </div>
          }
        />

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {roleSummaries.map((role) => (
            <MetricCard
              key={role.id}
              label={role.label}
              value={`${role.capabilities.length}/${ALL_CAPABILITIES.length}`}
              description={role.description}
              tone={role.presetRole ? roleTone(role.presetRole) : 'neutral'}
              action={
                role.custom ? (
                  <Button size="sm" variant="outline" onClick={() => openEditCustomRoleSheet(role)}>
                    Modifier
                  </Button>
                ) : (
                  <Button asChild size="sm" variant="outline">
                    <Link to={`/roles-permissions/${role.id}`}>Modifier</Link>
                  </Button>
                )
              }
            />
          ))}
        </section>

        <Card className="gap-4">
          <CardHeader>
            <CardTitle>Matrice des permissions</CardTitle>
            <CardDescription>
              Comparer rapidement ce que chaque rôle peut consulter, traiter ou administrer.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={CAPABILITY_ROWS}
              mobileCardLabel="Permissions par rôle"
              mobileCard={(row) => (
                <DataCard
                  eyebrow={row.group}
                  title={row.label}
                  meta={
                    <>
                      {roleSummaries.map((role) => (
                        <span key={role.id} className="text-muted-foreground">
                          {role.label} : {role.capabilities.includes(row.capability) ? 'oui' : 'non'}
                        </span>
                      ))}
                    </>
                  }
                />
              )}
              emptyState={
                <EmptyState
                  title="Aucune permission"
                  description="Les permissions configurables apparaîtront ici."
                />
              }
            />
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={confirmSaveOpen} onOpenChange={setConfirmSaveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {roleDraft.id ? 'Enregistrer les modifications du rôle ?' : 'Créer ce rôle ?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              « {roleDraft.label.trim()} » disposera de {roleDraft.capabilities.length}{' '}
              permission(s). La définition d'un rôle est une action sensible et sera journalisée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={applyCustomRole}>Confirmer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
